import React, { useState, useRef, useEffect, useCallback, ChangeEvent } from "react";

import { styled } from "@superset-ui/core";
import { UploadOutlined, CloseCircleFilled } from "@ant-design/icons";
import { Input } from "src/components/Input";
import ControlHeader from "../../../ControlHeader";

/**
 * ImageUploadControl 패널로 전달되는 props 정의
 * @typedef {Object} ImageUploadControlProps
 * @property {string} label label 정보(aria-label 속성에 사용, html 요소의 설명용 텍스트로 화면상에 보여지는 label은 아님)
 * @property {string} placeholder input 에 보여질 placeholder
 * @property {Function} onChange 업데이트 된 정보들을 store에 저장하는 onchange 함수
 * @property {string} value 문자열로 변환된 파일 정보
 */
export interface ImageUploadControlProps {
    label: string;
    placeholder?: string;
    onChange: (fileStr: string | undefined) => void;
    value?: string;
}

/**
 * 실제 업로드된 file의 데이터를 사용하기 위한 fileData 구조 정의 (store 저장 시, 문자열 변환)
 * @typedef {Object} ImageFileData
 * @property {string | ArrayBuffer | null} file file 객체 정보
 * @property {string} fileName 파일 명
 * @property {Function} fileSize 파일 크기
 * @property {string} fileType 파일 타입
 */
export interface ImageFileData {
    file: string | ArrayBuffer | null;
    fileName: string;
    fileSize: number;
    fileType: string;
}

const StyledImageUploadControlContainer = styled.div`
    position: relative;
    input[type="file"] {
        display: none;
    }
`;

const StyledButtonContainer = styled.div`
    ${({ theme }) => `
        display: flex;
        justify-content: flex-end;
        position: absolute;
        width: 24px;
        right: 22px;
        top: 50%;
        transform: translate(50%, 50%);
        gap: 4px;
        color: ${theme.colors.icon.base}
    `}
`;

const StyledDeleteButton = styled(CloseCircleFilled)`
    ${({ theme }) => `
        cursor: pointer;
        &:hover {
            color: ${theme.colors.icon.hover}
        }
    `}
`;

const StyledUploadButton = styled(UploadOutlined)`
    ${({ theme }) => `
        cursor: pointer;
        &:hover {
            color: ${theme.colors.icon.hover}
        }
    `}
`;

const StyledInput = styled(Input)`
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    padding-right: 40px;
`;

const ImageUploadControl = (props: ImageUploadControlProps): JSX.Element => {
    const { label, placeholder, onChange, value } = props;

    const fileRef = useRef<HTMLInputElement>(null);
    const [fileName, setFileName] = useState<string>("");

    /**
     * @description [useEffect hooks] 기존 업데이트 된 value가 있는 경우 파일명 표시
     */
    useEffect(() => {
        if (value) {
            const parseJsonFileData = JSON.parse(value);
            setFileName(parseJsonFileData.fileName);
        }
    }, [value]);

    /**
     * @name handleDeleteClick
     * @function
     * @description 삭제 버튼 클릭 이벤트 핸들러
     * @return {void}
     */
    const handleDeleteClick = useCallback((): void => {
        onChange(undefined);
        if (fileRef.current) {
            fileRef.current.value = "";
        }
        setFileName("");
    }, [onChange]);

    /**
     * @name handleUploadClick
     * @function
     * @description 업로드 버튼 클릭 이벤트 핸들러
     * @return {void}
     */
    const handleUploadClick = (): void => {
        fileRef.current?.click();
    };

    /**
     * @name handleFileInputChange
     * @function
     * @description 파일 데이터 변경 이벤트 핸들러
     * @param {ChangeEvent<HTMLInputElement>} e
     * @return {void}
     */
    const handleFileInputChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            if (e.target.files && e.target.files.length > 0) {
                const file = e.target.files[0];
                setFileName(file.name);

                const fileData: ImageFileData = {
                    file: null,
                    fileName: file.name,
                    fileSize: file.size,
                    fileType: file.type,
                };

                const reader = new FileReader();
                if (file) {
                    reader.readAsDataURL(file);
                    reader.onloadend = () => {
                        fileData.file = reader.result;
                        const json = JSON.stringify(fileData);
                        onChange(json);
                    };
                }
            }
        },
        [onChange]
    );

    return (
        <StyledImageUploadControlContainer>
            <ControlHeader {...props} />
            <StyledInput placeholder={placeholder} value={fileName} aria-label={label} />
            <StyledButtonContainer>
                {fileName && <StyledDeleteButton size={12} onClick={handleDeleteClick} />}
                <StyledUploadButton size={12} onClick={handleUploadClick} />
            </StyledButtonContainer>
            <input type="file" accept="image/*" ref={fileRef} onChange={handleFileInputChange} />
        </StyledImageUploadControlContainer>
    );
};

export default ImageUploadControl;
